import { formatExamTime, EXAM_TYPE_LABEL } from '@exam-center/exam-event-management';
import { type ExamType, TagCategory } from '@exam-center/graphql';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Form,
  FormSearchMultipleSelectionWithSelectAllOption,
  type OptionSelectionProps,
  Text,
} from '@shared/components';
import { formatDate } from '@shared/utils';
import { useEffect, useMemo, useRef } from 'react';
import { FormProvider, useForm } from 'react-hook-form';

import {
  type ExportConfirmationModalFormInput,
  ExportConfirmationModalSchema,
} from './export-confirmation-modal-schema';

export interface ExportConfirmationModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (examCourseIds: string[] | null) => void;
  onSelectionChange?: (examCourseIds: string[] | null) => void;
  examStartedAt: Date;
  examEndedAt: Date;
  examRound: number;
  category: TagCategory;
  totalRecords: number;
  examType: ExamType;
  courseOptions: OptionSelectionProps[];
  defaultCourseIds: string[];
  isTotalLoading?: boolean;
  loading?: boolean;
}

const TAG_CATEGORY_LABEL: Partial<Record<TagCategory, string>> = {
  [TagCategory.Part]: 'รายหมวด',
  [TagCategory.Subject]: 'รายวิชา',
};

export default function ExportConfirmationModal(props: Readonly<ExportConfirmationModalProps>) {
  const {
    open,
    onClose,
    onConfirm,
    onSelectionChange,
    examStartedAt,
    examEndedAt,
    examRound,
    category,
    totalRecords,
    examType,
    courseOptions,
    defaultCourseIds,
    isTotalLoading = false,
    loading = false,
  } = props;

  const formattedDate = formatDate(examStartedAt);
  const formattedTime = formatExamTime(examStartedAt, examEndedAt);

  const defaultCourseOptions = useMemo(
    () =>
      courseOptions
        .filter((option) => defaultCourseIds.includes(String(option.value)))
        .map((option) => ({ label: option.label, value: String(option.value) })),
    [courseOptions, defaultCourseIds],
  );

  const formContext = useForm<ExportConfirmationModalFormInput>({
    resolver: zodResolver(ExportConfirmationModalSchema),
    mode: 'onChange',
    defaultValues: { examCourses: defaultCourseOptions },
  });

  // Reset the selection to the default course each time the modal opens.
  // Guard on the open transition so a parent re-render (e.g. the aggregate
  // total refetch) doesn't re-run the reset mid-interaction and wipe the
  // user's selection back to the default.
  const wasOpen = useRef(false);
  useEffect(() => {
    if (open && !wasOpen.current) {
      formContext.reset({ examCourses: defaultCourseOptions });
    }
    wasOpen.current = open;
  }, [open, defaultCourseOptions, formContext]);

  const selectedCourses = formContext.watch('examCourses');
  const isConfirmDisabled = !selectedCourses?.length || isTotalLoading || loading;

  // `FormSearchMultipleSelectionWithSelectAllOption` stores a single
  // `{ value: 'all' }` sentinel instead of expanding to every option. When
  // every course is selected, resolve to `null` so callers treat it as "all
  // courses" instead of an explicit id list.
  const resolveExamCourseIds = (
    options: ExportConfirmationModalFormInput['examCourses'],
  ): string[] | null => {
    const isSelectAll = options.some((option) => option.value === 'all');
    return isSelectAll ? null : options.map((option) => option.value);
  };

  // Surface the resolved selection so the container can refetch the aggregate
  // total. Keyed on the serialized values to avoid firing on every render.
  const selectionKey = JSON.stringify(selectedCourses?.map((option) => option.value) ?? []);
  useEffect(() => {
    if (!open) return;
    onSelectionChange?.(resolveExamCourseIds(selectedCourses ?? []));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, selectionKey]);

  const handleConfirm = formContext.handleSubmit((data) => {
    onConfirm(resolveExamCourseIds(data.examCourses));
  });

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      slotProps={{
        paper: {
          sx: { minWidth: '440px' },
        },
      }}
    >
      <FormProvider {...formContext}>
        <Form onSubmit={handleConfirm}>
          <DialogTitle onClose={onClose}>ยืนยันการส่งออกข้อมูล</DialogTitle>
          <Divider />
          <DialogContent>
            <Box
              sx={(theme) => ({
                display: 'flex',
                flexDirection: 'column',
                gap: theme.spacing(3),
              })}
            >
              <Text variant='body1Semibold'>สรุปข้อมูลที่ต้องการ</Text>

              <Box
                sx={(theme) => ({
                  display: 'flex',
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
                  วันที่สอบ:
                </Text>
                <Text>{`${formattedDate}, ${formattedTime}`}</Text>
              </Box>

              <Box
                sx={(theme) => ({
                  display: 'flex',
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
                  รอบสอบที่:
                </Text>
                <Text>{examRound}</Text>
              </Box>

              <Box
                sx={(theme) => ({
                  display: 'flex',
                  gap: theme.spacing(2),
                  alignItems: 'flex-start',
                })}
              >
                <Text variant='body1Semibold' sx={{ minWidth: '120px', mt: '10px' }}>
                  หลักสูตร:
                </Text>
                <FormSearchMultipleSelectionWithSelectAllOption
                  id='export-confirmation-exam-courses'
                  name='examCourses'
                  options={courseOptions}
                  placeholder='เลือกหลักสูตร'
                  isOptionEqualToValue={(option, selected) => option.value === selected.value}
                  sx={{ flex: 1 }}
                />
              </Box>

              <Box
                sx={(theme) => ({
                  display: 'flex',
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
                  ประเภทคะแนน:
                </Text>
                <Text>{TAG_CATEGORY_LABEL[category] ?? ''}</Text>
              </Box>

              <Box
                sx={(theme) => ({
                  display: 'flex',
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
                  Exam Type:
                </Text>
                <Text>{EXAM_TYPE_LABEL[examType]}</Text>
              </Box>

              <Box
                sx={(theme) => ({
                  display: 'flex',
                  gap: theme.spacing(2),
                })}
              >
                <Text variant='body1Semibold' sx={{ minWidth: '120px' }}>
                  รวมทั้งหมด:
                </Text>
                <Text>{isTotalLoading ? '...' : `${totalRecords} รายการ`}</Text>
              </Box>
            </Box>
          </DialogContent>
          <Divider />
          <DialogActions>
            <Button
              variant='outlined'
              color='secondary'
              size='large'
              onClick={onClose}
              disabled={loading}
            >
              ยกเลิก
            </Button>
            <Button
              type='submit'
              variant='contained'
              color='primary'
              size='large'
              loading={loading}
              disabled={isConfirmDisabled}
              sx={{ minWidth: '140px' }}
            >
              ส่งออกข้อมูล
            </Button>
          </DialogActions>
        </Form>
      </FormProvider>
    </Dialog>
  );
}
